import type { ClientIntegrationSummaryDto } from '@/apis/backend/generated/models/clientIntegrationSummaryDto';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import {
  useClientSummariesControllerGetIntegrationSummaries,
} from '@/apis/backend/generated/client-summaries/client-summaries';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import { alpha, styled } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';

import { paths } from 'src/routes/paths';
import { Iconify } from 'src/components/iconify';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/settings/client/brand/integrations')({
  component: IntegrationsList,
});

function IntegrationsList() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { activeClient, switchClient } = useOrganization();
  const { orgId } = Route.useParams();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: integrationSummaries, isLoading } =
    useClientSummariesControllerGetIntegrationSummaries(orgId, {
      query: { enabled: !!orgId },
    });

  const filtered = useMemo(() => {
    const items = integrationSummaries?.items ?? [];
    const sorted = [...items].sort((a, b) => a.clientName.localeCompare(b.clientName));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase().trim();
    return sorted.filter((item) => item.clientName.toLowerCase().includes(q));
  }, [integrationSummaries?.items, searchQuery]);

  const handleOpenDetail = (clientId: string) => {
    if (clientId !== activeClient?.id) {
      switchClient(clientId);
    }
    navigate({ to: paths.dashboard.orgSettings(orgId).client.clientIntegrations(clientId) });
  };

  return (
    <>
      <SearchField value={searchQuery} onChange={setSearchQuery} count={filtered.length} />
      {isLoading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={88} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((item) => (
            <IntegrationSummaryCard
              key={item.clientId}
              item={item}
              isActive={item.clientId === activeClient?.id}
              onClick={() => handleOpenDetail(item.clientId)}
            />
          ))}
          {filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {searchQuery
                ? t('brandIntegrations.empty.noMatch', 'No clients match your search')
                : t('brandIntegrations.empty.noClients', 'No clients found')}
            </Typography>
          )}
        </Stack>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function SearchField({
  value,
  onChange,
  count,
}: {
  value: string;
  onChange: (v: string) => void;
  count: number;
}) {
  const { t } = useTranslation('settings');

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <TextField
        autoFocus
        size="small"
        placeholder={t('brandIntegrations.search.placeholder', 'Search clients...')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          width: 260,
          '& .MuiOutlinedInput-root': { fontSize: '0.8125rem' },
        }}
      />
      <Typography variant="caption" color="text.disabled">
        {t('brandIntegrations.search.count', '{{count}} client', { count })}
      </Typography>
    </Stack>
  );
}

const INTEGRATION_LABELS: Record<string, string> = {
  shopify: 'Shopify',
  klaviyo: 'Klaviyo',
  mailchimp: 'Mailchimp',
  campaigner: 'Campaigner',
  google_drive: 'Google Drive',
};

function IntegrationSummaryCard({
  item,
  isActive,
  onClick,
}: {
  item: ClientIntegrationSummaryDto;
  isActive: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('settings');
  const { integrations } = item;

  return (
    <ClientCard onClick={onClick}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
        <Avatar
          variant="rounded"
          sx={{
            width: 40,
            height: 40,
            bgcolor: isActive ? (theme) => alpha(theme.palette.primary.main, 0.12) : 'grey.100',
            color: isActive ? 'primary.main' : 'text.secondary',
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          {item.clientName.charAt(0).toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {item.clientName}
            </Typography>
            {isActive && (
              <Chip label={t('brandIntegrations.card.active', 'Active')} size="small" sx={{ height: 20, fontSize: '0.6875rem' }} color="primary" />
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            {integrations.total === 0 ? (
              <Typography variant="caption" color="text.disabled">
                {t('brandIntegrations.card.noIntegrations', 'No integrations')}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t('brandIntegrations.card.activeCount', '{{count}} active', { count: integrations.active })}
                </Typography>
                {integrations.error > 0 && (
                  <Chip
                    label={t('brandIntegrations.card.errorCount', '{{count}} error', { count: integrations.error })}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.625rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                )}
                <Stack direction="row" spacing={0.5} sx={{ ml: 0.5 }}>
                  {integrations.types.map((type) => (
                    <Chip
                      key={type}
                      label={INTEGRATION_LABELS[type] || type}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.625rem', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      </Stack>

      <Iconify icon="eva:chevron-right-fill" width={20} sx={{ color: 'text.disabled', flexShrink: 0 }} />
    </ClientCard>
  );
}

// ----------------------------------------------------------------------

const ClientCard = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(2, 2.5),
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  textAlign: 'left',
  gap: theme.spacing(1),
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));
